import {
  editionVerkaufbar,
  FUNKTIONSNAMEN,
  funktionen,
  type LizenzLaufzeit,
  type LizenzVorschau,
  type PartnerEdition,
  type PartnerKunde,
} from '@nac2s/shared'
import { Minus, Plus } from '@untitledui/icons'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { ButtonGroup, ButtonGroupItem } from '@/components/base/button-group/button-group'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { Select } from '@/components/base/select/select'
import { Knopf } from '@/teile/Knopf'
import { Merkmalsliste } from '@/teile/Merkmalsliste'
import { Fehler, Laedt, Leer } from '@/teile/Tabelle'
import { cx } from '@/utils/cx'
import { ApiError } from '../api/client.js'
import { ladeEditionen, ladeKunden, lizenzKaufen, lizenzVorschau } from '../api/partner.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Katalog des Partners (ADR-0015 Nachtrag): Was netable ihm zu verkaufen gibt,
 * zum Preis, der für ihn gilt. Der Weg: erst der Kunde aus der eigenen Liste,
 * dann die Edition mit der Menge, dann die Laufzeit. Alles, was daraus folgt —
 * Preis, Testzeitraum, Betrag, Fälligkeit, ob der Kauf überhaupt geht — rechnet
 * der Server (`lizenzVorschau`); diese Seite zeigt es nur an. Der Kauf selbst
 * ist derselbe Endpunkt wie im Kaufdialog am Kunden.
 */

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; editionen: PartnerEdition[]; kunden: PartnerKunde[] }
  | { status: 'fehler'; meldung: string }

type Vorschauzustand =
  | { status: 'leer' }
  | { status: 'laedt' }
  | { status: 'da'; vorschau: LizenzVorschau }
  | { status: 'fehler'; meldung: string }

function euro(cent: number): string {
  return (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

function datum(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/** Der Satz, der einen Abstieg sperrt — aus dem, was der Server über die laufende Lizenz sagt. */
export function sperrsatz(kundeName: string, v: LizenzVorschau): string | null {
  if (v.abstieg === null || v.bisher === null) return null
  const ende = datum(v.bisher.laufzeitEnde)
  const lizenz = `${kundeName} hat eine Lizenz ${v.bisher.editionName} mit Laufzeit bis ${ende}.`
  switch (v.abstieg) {
    case 'edition':
      return `${lizenz} ${v.editionName} wäre ein Abstieg — der ist erst zum Ende der Laufzeit möglich, ein Aufstieg jederzeit.`
    case 'laufzeit':
      return `${lizenz} Ein Wechsel auf monatliche Laufzeit ist erst danach möglich.`
    case 'menge':
      return `${lizenz} Ihre Mindestmenge sind ${v.bisher.mindestmenge} Seeds — weniger sind erst danach möglich, mehr jederzeit.`
  }
}

export function Katalog() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [kundeHost, setKundeHost] = useState('')
  const [edition, setEdition] = useState('')
  const [laufzeit, setLaufzeit] = useState<LizenzLaufzeit>('mindest')
  const [seeds, setSeeds] = useState('1')
  const [vorschau, setVorschau] = useState<Vorschauzustand>({ status: 'leer' })
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const { zustand: sitzung } = useSitzung()
  const darfKaufen =
    sitzung.status === 'angemeldet' && sitzung.sitzung.rechte.includes('kunden.kaufmaennisch')
  const navigate = useNavigate()

  useEffect(() => {
    let aktuell = true
    Promise.all([ladeEditionen(), ladeKunden()])
      .then(([k, kunden]) => {
        if (!aktuell) return
        const verkaufbar = k.editionen.filter((e) => editionVerkaufbar(e))
        setZustand({ status: 'da', editionen: verkaufbar, kunden })
        setEdition((alt) => alt || (verkaufbar[0]?.key ?? ''))
      })
      .catch((fehler: unknown) => {
        if (aktuell)
          setZustand({
            status: 'fehler',
            meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
          })
      })
    return () => {
      aktuell = false
    }
  }, [])

  const editionen = zustand.status === 'da' ? zustand.editionen : []
  const kunden =
    zustand.status === 'da' ? zustand.kunden.filter((k) => k.zustand !== 'geloescht') : []
  const ziel = editionen.find((e) => e.key === edition)
  const kunde = kunden.find((k) => k.host === kundeHost)
  const menge = /^\d{1,6}$/.test(seeds.trim()) ? Number(seeds) : null

  // Jede Änderung an Kunde, Edition, Menge oder Laufzeit fragt den Server,
  // was daraus würde — kurz verzögert, damit Tippen nicht je Ziffer fragt.
  const zielKey = ziel?.key ?? null
  const kundeHostGewaehlt = kunde?.host ?? null
  useEffect(() => {
    if (!kundeHostGewaehlt || !zielKey || menge === null) {
      setVorschau({ status: 'leer' })
      return
    }
    let aktuell = true
    setVorschau({ status: 'laedt' })
    const t = setTimeout(() => {
      lizenzVorschau(kundeHostGewaehlt, { edition: zielKey, laufzeit, mindestmenge: menge })
        .then((v) => {
          if (aktuell) setVorschau({ status: 'da', vorschau: v })
        })
        .catch((fehler: unknown) => {
          if (aktuell)
            setVorschau({
              status: 'fehler',
              meldung:
                fehler instanceof ApiError ? fehler.message : 'Die Vorschau ist nicht gekommen.',
            })
        })
    }, 250)
    return () => {
      aktuell = false
      clearTimeout(t)
    }
  }, [kundeHostGewaehlt, zielKey, laufzeit, menge])

  const v = vorschau.status === 'da' ? vorschau.vorschau : null
  const sperre = v && kunde ? sperrsatz(kunde.anzeigename, v) : null

  function schritt(delta: number) {
    setSeeds(String(Math.max(0, Math.min(999_999, (menge ?? 0) + delta))))
  }

  async function kaufen() {
    if (!ziel || !kunde || menge === null || !v || sperre) return
    setLaeuft(true)
    setMeldung(null)
    try {
      const lizenz = await lizenzKaufen(kunde.host, {
        edition: ziel.key,
        laufzeit,
        mindestmenge: menge,
      })
      setMeldung({
        art: 'ok',
        text: `Gekauft: ${ziel.name} für ${kunde.anzeigename}, ${menge} Seeds. Testzeitraum bis ${datum(lizenz.bezahltAb)}, danach ${laufzeit === 'monatlich' ? 'monatlich' : `Mindestlaufzeit bis ${datum(lizenz.laufzeitEnde)}`}.`,
      })
    } catch (fehler: unknown) {
      setMeldung({
        art: 'fehler',
        text:
          fehler instanceof ApiError && fehler.code === 'laufzeit_laeuft'
            ? 'Dieser Kunde hat eine laufende Mindestlaufzeit: Bis zu ihrem Ende geht nur ein Aufstieg.'
            : fehler instanceof ApiError
              ? fehler.message
              : 'Der Kauf ist nicht durchgegangen.',
      })
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div>
      <div className="relative overflow-hidden px-6 pt-6 pb-[18px]">
        <div
          aria-hidden="true"
          className="schimmer pointer-events-none absolute -top-[13rem] -right-[6rem] size-[25rem]"
        />
        <div className="relative">
          <p className="text-[13px] text-quaternary">Ihr Katalog</p>
          <h1 className="mt-4 font-display text-[26px] leading-[1.1] font-extrabold tracking-[-0.025em] text-primary">
            Katalog
          </h1>
          <p className="mt-1 text-sm text-tertiary">
            Editionen zu Ihren Preisen. Ein Kauf gilt für einen Kunden aus Ihrer Liste — legen Sie
            ihn vorher an.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6 px-6 pb-8">
        {zustand.status === 'laedt' ? (
          <Laedt>Katalog wird geladen …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : editionen.length === 0 ? (
          <Leer titel="Keine Edition im Katalog">
            netable hat Ihrem Katalog noch keine verkaufbare Edition zugewiesen.
          </Leer>
        ) : (
          <>
            <section className="flex flex-col gap-2">
              <h2 className="text-[13px] font-semibold text-primary">Kunde</h2>
              <div className="max-w-md">
                <Select
                  size="sm"
                  aria-label="Kunde"
                  placeholder="Kunde wählen"
                  items={kunden.map((k) => ({
                    id: k.host,
                    label: k.anzeigename,
                    supportingText: k.host,
                  }))}
                  selectedKey={kundeHost || null}
                  onSelectionChange={(k) => setKundeHost(String(k))}
                >
                  {(eintrag) => <Select.Item {...eintrag} />}
                </Select>
              </div>
              {kunden.length === 0 ? (
                <p className="text-sm text-tertiary">
                  Noch kein Kunde angelegt —{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="cursor-pointer text-primary underline"
                  >
                    zur Kundenliste
                  </button>
                  .
                </p>
              ) : kunde?.lizenz ? (
                <p className="text-[12.5px] text-tertiary">
                  Heute:{' '}
                  {editionen.find((e) => e.key === kunde.lizenz?.edition)?.name ??
                    kunde.lizenz.edition}
                  , {kunde.lizenz.mindestmenge} Seeds,{' '}
                  {kunde.lizenz.laufzeit === 'monatlich'
                    ? 'monatlich'
                    : `Mindestlaufzeit bis ${datum(kunde.lizenz.laufzeitEnde)}`}
                  .
                </p>
              ) : null}
            </section>

            <section className="flex flex-col gap-2">
              <h2 className="text-[13px] font-semibold text-primary">
                Edition, Laufzeit und Seeds
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {editionen.map((e) => {
                  const gewaehlt = e.key === edition
                  return (
                    // biome-ignore lint/a11y/noStaticElementInteractions: Die Fläche wählt mit der Maus; die Tastatur wählt über den Kopf-Knopf darin.
                    // biome-ignore lint/a11y/useKeyWithClickEvents: siehe oben
                    <div
                      key={e.key}
                      onClick={() => setEdition(e.key)}
                      className={cx(
                        'flex cursor-pointer flex-col rounded-2xl border p-5 transition-colors',
                        gewaehlt
                          ? 'border-akzent bg-akzent-flaeche'
                          : 'border-secondary bg-primary hover:border-rand-aktiv',
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <button
                          type="button"
                          aria-pressed={gewaehlt}
                          onClick={() => setEdition(e.key)}
                          className="cursor-pointer rounded-lg text-left focus-visible:outline-2 focus-visible:outline-akzent"
                        >
                          <p className="font-display text-lg font-bold text-primary">{e.name}</p>
                        </button>
                        <ButtonGroup
                          size="sm"
                          aria-label={`Laufzeit ${e.name}`}
                          className="shrink-0"
                          selectedKeys={gewaehlt ? [laufzeit] : []}
                          disallowEmptySelection={gewaehlt}
                          onSelectionChange={(auswahl) => {
                            const [w] = [...auswahl]
                            if (w !== 'monatlich' && w !== 'mindest') return
                            setEdition(e.key)
                            setLaufzeit(w)
                          }}
                        >
                          <ButtonGroupItem id="mindest">Jährlich</ButtonGroupItem>
                          <ButtonGroupItem id="monatlich">Monatlich</ButtonGroupItem>
                        </ButtonGroup>
                      </div>
                      <p className="mt-2 text-sm text-tertiary tabular-nums">
                        {e.seedCent === null || e.seedMonatlichCent === null
                          ? 'Preis noch nicht hinterlegt'
                          : laufzeit === 'monatlich'
                            ? `${euro(e.seedMonatlichCent)} je Seed und Monat, netto`
                            : `${euro(e.seedCent)} je Seed und Monat, netto`}
                      </p>
                      <p className="mt-0.5 text-[12px] text-quaternary">
                        {laufzeit === 'monatlich'
                          ? 'Monatlich kündbar · jährlich −'
                          : `Mindestlaufzeit ${e.mindestlaufzeitMonate} Monate · jährlich −`}
                        {e.monatlichAufschlagProzent} %
                      </p>
                      <div className="mt-3">
                        <Merkmalsliste
                          titel="Enthalten"
                          merkmale={funktionen.map((f) => ({
                            name: FUNKTIONSNAMEN[f].name,
                            enthalten: e.funktionen.includes(f),
                          }))}
                        />
                      </div>
                      {gewaehlt ? (
                        <div className="mt-4 flex flex-col gap-1.5 border-t border-akzent/40 pt-4">
                          <span className="text-[12.5px] font-medium text-primary">
                            Seeds (Mindestmenge)
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              color="secondary"
                              aria-label="Einen Seed weniger"
                              iconLeading={Minus}
                              isDisabled={(menge ?? 0) <= 0}
                              onClick={() => schritt(-1)}
                            />
                            <div className="w-24">
                              <Input
                                aria-label="Anzahl Seeds"
                                size="sm"
                                inputMode="numeric"
                                value={seeds}
                                onChange={setSeeds}
                              />
                            </div>
                            <Button
                              size="sm"
                              color="secondary"
                              aria-label="Einen Seed mehr"
                              iconLeading={Plus}
                              onClick={() => schritt(1)}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </section>

            <section className="flex flex-col gap-4 rounded-2xl border border-secondary p-5">
              <h2 className="text-[13px] font-semibold text-primary">
                Kaufen{ziel ? `: ${ziel.name}` : ''}
                {kunde ? ` für ${kunde.anzeigename}` : ''}
              </h2>
              {!kunde ? (
                <p className="text-sm text-tertiary">Wählen Sie oben einen Kunden.</p>
              ) : menge === null ? (
                <Fehler>Die Anzahl Seeds muss eine ganze Zahl sein.</Fehler>
              ) : vorschau.status === 'fehler' ? (
                <Fehler>{vorschau.meldung}</Fehler>
              ) : v ? (
                <>
                  <p className="rounded-xl bg-akzent-flaeche p-3 text-[12.5px] text-akzent-tief">
                    {v.imTestzeitraum
                      ? `Testzeitraum bis ${datum(v.bezahltAb)}.`
                      : `Die laufende Lizenz wird fortgesetzt — bezahlt seit ${datum(v.bezahltAb)}.`}
                  </p>
                  <dl className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <dt className="text-[12px] text-quaternary">Gesamtbetrag je Monat, netto</dt>
                      <dd className="mt-0.5 font-display text-2xl font-extrabold tabular-nums text-primary">
                        {v.gesamtJeMonatCent === null ? '—' : euro(v.gesamtJeMonatCent)}
                      </dd>
                      <dd className="text-[12px] text-tertiary tabular-nums">
                        {v.preisJeSeedCent === null
                          ? 'Ein Preis ist für diese Edition noch nicht hinterlegt.'
                          : `${v.mindestmenge} × ${euro(v.preisJeSeedCent)} je Seed${v.laufzeit === 'monatlich' ? ' (monatlich kündbar)' : ` (jährlich −${v.monatlichAufschlagProzent} %)`}`}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[12px] text-quaternary">Fällig ab</dt>
                      <dd className="mt-0.5 font-display text-2xl font-extrabold tabular-nums text-primary">
                        {datum(v.bezahltAb)}
                      </dd>
                      <dd className="text-[12px] text-tertiary">
                        dann monatlich der höhere Wert aus Seeds und Mindestmenge
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[12px] text-quaternary">
                        {v.laufzeit === 'monatlich' ? 'Laufzeit' : 'Mindestlaufzeit bis'}
                      </dt>
                      <dd className="mt-0.5 font-display text-2xl font-extrabold tabular-nums text-primary">
                        {v.laufzeit === 'monatlich' ? 'monatlich' : datum(v.laufzeitEnde)}
                      </dd>
                      <dd className="text-[12px] text-tertiary">
                        {v.laufzeit === 'monatlich'
                          ? 'zum Monatsende kündbar'
                          : `${v.mindestlaufzeitMonate} Monate ab ${datum(v.start)}`}
                      </dd>
                    </div>
                  </dl>
                  {sperre ? <Fehler>{sperre}</Fehler> : null}
                </>
              ) : (
                <p className="text-sm text-tertiary">Wird berechnet …</p>
              )}
              {meldung ? (
                meldung.art === 'ok' ? (
                  <p
                    role="status"
                    className="rounded-xl bg-success-primary p-3 text-sm text-success-primary"
                  >
                    {meldung.text}
                  </p>
                ) : (
                  <Fehler>{meldung.text}</Fehler>
                )
              ) : null}
              <div className="flex justify-end">
                <Knopf
                  stark
                  disabled={!darfKaufen || laeuft || !v || sperre !== null}
                  onClick={() => void kaufen()}
                >
                  {laeuft ? 'Wird gekauft …' : 'Kaufen'}
                </Knopf>
              </div>
              {!darfKaufen ? (
                <p className="text-[11.5px] text-quaternary">Kaufen dürfen Vertrieb und Admin.</p>
              ) : null}
            </section>
          </>
        )}
      </div>
    </div>
  )
}
